import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '@/components/ui/dialog';
import { User, Store, ShieldCheck, Check, Plus, X, Users } from 'lucide-react';
import { toast } from 'sonner';

// Renders a compact "Switch account" trigger that opens a dialog
// listing all persisted sessions with 1-tap swap.
// If `controlled` is true, the parent controls open/setOpen (needed when
// used inside a DropdownMenuItem to avoid focus conflicts).
export function AccountSwitcher({ trigger, open: openProp, onOpenChange }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof openProp === 'boolean';
  const open = isControlled ? openProp : internalOpen;
  const setOpen = isControlled ? onOpenChange : setInternalOpen;

  const { sessions, activeSessionId, switchSession, removeSession } = useAuth();
  const { language } = useI18n();
  const navigate = useNavigate();

  const iconFor = (type) => {
    if (type === 'manager') return ShieldCheck;
    if (type === 'business_owner') return Store;
    return User;
  };

  const labelFor = (type) => {
    if (type === 'manager') return language === 'es' ? 'Administrador' : 'Administrator';
    if (type === 'business_owner') return language === 'es' ? 'Negocio' : 'Business';
    return language === 'es' ? 'Cliente' : 'Client';
  };

  const handleSwitch = (session) => {
    if (session.id === activeSessionId) return;
    switchSession(session.id);
    toast.success(language === 'es' ? `Ahora estás como ${session.label}` : `Now signed in as ${session.label}`);
    setOpen(false);
    setTimeout(() => {
      if (session.type === 'business_owner' || session.type === 'manager') {
        navigate('/business/dashboard');
      } else {
        navigate('/search');
      }
    }, 50);
  };

  const handleRemove = (e, session) => {
    e.stopPropagation();
    const confirmMsg = language === 'es'
      ? `¿Cerrar sesión de "${session.label}"?`
      : `Sign out of "${session.label}"?`;
    if (!window.confirm(confirmMsg)) return;
    removeSession(session.id);
    toast.success(language === 'es' ? 'Sesión cerrada' : 'Signed out');
  };

  const handleAdd = () => {
    setOpen(false);
    navigate('/login?add=1');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-md" data-testid="account-switcher-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[#F05D5E]" />
            {language === 'es' ? 'Cambiar de cuenta' : 'Switch account'}
          </DialogTitle>
          <DialogDescription>
            {language === 'es'
              ? 'Cambia entre tus cuentas sin cerrar sesión. Tus datos quedan guardados en este dispositivo.'
              : 'Switch between your accounts without logging out. Data is kept on this device.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {sessions.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">
              {language === 'es' ? 'No hay cuentas guardadas' : 'No saved accounts'}
            </p>
          )}
          {sessions.map(s => {
            const Icon = iconFor(s.type);
            const isActive = s.id === activeSessionId;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => handleSwitch(s)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                  isActive
                    ? 'border-[#F05D5E] bg-[#F05D5E]/5'
                    : 'border-border/70 hover:border-[#F05D5E]/60 hover:bg-muted/50'
                }`}
                data-testid={`account-switch-${s.id}`}
              >
                <Avatar className="h-11 w-11 shrink-0">
                  <AvatarImage src={s.avatar || undefined} />
                  <AvatarFallback className="bg-[#F05D5E]/10 text-[#F05D5E] font-bold">
                    {s.label?.[0]?.toUpperCase() || '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{s.label}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon className="h-3 w-3" />
                    {labelFor(s.type)}
                  </p>
                </div>
                {isActive ? (
                  <span className="text-[11px] font-medium text-[#F05D5E] flex items-center gap-1 shrink-0">
                    <Check className="h-3 w-3" /> {language === 'es' ? 'Activa' : 'Active'}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => handleRemove(e, s)}
                    className="p-1 rounded-full text-muted-foreground hover:bg-red-50 hover:text-red-600 shrink-0"
                    aria-label="remove"
                    data-testid={`account-remove-${s.id}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </button>
            );
          })}
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full mt-2"
          onClick={handleAdd}
          data-testid="account-add-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {language === 'es' ? 'Agregar otra cuenta' : 'Add another account'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
